/**
 * NotificationsPage — the notification & messaging engine UI (FND-008).
 *
 * Two surfaces:
 *   - Delivery log (GET /notifications/deliveries) — per-message lifecycle events
 *     with channel + status, surfacing the TCPA suppression status explicitly.
 *   - Templates (GET /notifications/templates) — the message-template catalogue
 *     with channel, variables, and version.
 *
 * Shapes verified against services/notifications/schemas.py; both lists degrade
 * gracefully to a friendly empty/disabled state on 404.
 */
import { useMemo, useState, type CSSProperties } from 'react';
import {
  useNotifications,
  type DeliveryEvent,
  type MessageTemplate,
} from '../hooks/useNotifications';
import { colors, radius } from '../theme';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge, statusToSemantic } from '../components/StatusBadge';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { Toolbar } from '../components/Toolbar';
import { useShellSlot } from '../components/shellContext';

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

const CHANNEL_ICON: Record<string, string> = {
  email: '✉',
  sms: '💬',
  push: '🔔',
  inbox: '📥',
};

type Tab = 'deliveries' | 'templates';

export function NotificationsPage() {
  useShellSlot({ breadcrumb: 'Notifications' });
  const {
    deliveries,
    templates,
    deliveriesAvailable,
    templatesAvailable,
    isLoading,
    error,
    refresh,
  } = useNotifications();

  const [tab, setTab] = useState<Tab>('deliveries');

  const suppressed = deliveries.filter((d) => d.status === 'suppressed_tcpa').length;
  const usageByTemplate = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of deliveries) {
      map.set(d.template_id, (map.get(d.template_id) ?? 0) + 1);
    }
    return map;
  }, [deliveries]);

  const deliveryColumns: Column<DeliveryEvent>[] = [
    {
      key: 'channel',
      header: 'Channel',
      render: (d) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span aria-hidden>{CHANNEL_ICON[d.channel] ?? '•'}</span>
          {d.channel}
        </span>
      ),
    },
    { key: 'to', header: 'To', render: (d) => <span style={styles.mono}>{d.to}</span> },
    { key: 'template', header: 'Template', render: (d) => d.template_id },
    {
      key: 'status',
      header: 'Status',
      render: (d) => (
        <StatusBadge
          value={d.status}
          status={d.status === 'suppressed_tcpa' ? 'danger' : statusToSemantic(d.status)}
          label={d.status === 'suppressed_tcpa' ? 'Suppressed (TCPA)' : undefined}
        />
      ),
    },
    { key: 'reason', header: 'Reason', render: (d) => d.reason ?? '—' },
    { key: 'when', header: 'When', render: (d) => fmtDateTime(d.occurred_at) },
  ];

  const templateColumns: Column<MessageTemplate>[] = [
    {
      key: 'name',
      header: 'Template',
      render: (t) => (
        <div>
          <div style={{ fontWeight: 600 }}>{t.name || t.template_id}</div>
          {t.subject && <div style={styles.muted}>{t.subject}</div>}
        </div>
      ),
    },
    {
      key: 'channel',
      header: 'Channel',
      render: (t) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span aria-hidden>{CHANNEL_ICON[t.channel] ?? '•'}</span>
          {t.channel}
        </span>
      ),
    },
    {
      key: 'vars',
      header: 'Variables',
      render: (t) =>
        t.variables.length ? (
          <span style={styles.mono}>{t.variables.map((v) => `{${v}}`).join(' ')}</span>
        ) : (
          '—'
        ),
    },
    { key: 'state', header: 'State variant', render: (t) => t.state ?? 'default' },
    { key: 'version', header: 'Version', align: 'right', render: (t) => `v${t.version}` },
    {
      key: 'usage',
      header: 'Sends (log)',
      align: 'right',
      render: (t) => usageByTemplate.get(t.template_id) ?? 0,
    },
  ];

  return (
    <>
      <PageHeader
        title="Notifications & Messaging"
        subtitle="Delivery audit trail and message templates for the notification engine (FND-008). SMS and automated outreach are gated by TCPA consent — suppressed sends are logged, never silently dropped."
        actions={
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
        }
      />

      {error && (
        <p style={styles.error} role="alert">
          {error.message}
        </p>
      )}

      <div style={styles.kpiStrip}>
        <Kpi label="Delivery events" value={String(deliveries.length)} />
        <Kpi label="Templates" value={String(templates.length)} />
        <Kpi
          label="TCPA-suppressed"
          value={String(suppressed)}
          accent={suppressed > 0 ? colors.danger : colors.success}
        />
      </div>

      <Toolbar style={{ marginBottom: 16 }}>
        <div style={styles.tabs}>
          <TabBtn active={tab === 'deliveries'} onClick={() => setTab('deliveries')}>
            Delivery log
          </TabBtn>
          <TabBtn active={tab === 'templates'} onClick={() => setTab('templates')}>
            Templates
          </TabBtn>
        </div>
      </Toolbar>

      {tab === 'deliveries' ? (
        <Card title="Delivery / audit trail" padded={false}>
          {isLoading ? (
            <div style={{ padding: 24 }}>
              <LoadingIndicator text="Loading deliveries…" />
            </div>
          ) : !deliveriesAvailable ? (
            <div style={{ padding: 24 }}>
              <p style={styles.muted}>The delivery log is not available yet.</p>
            </div>
          ) : (
            <DataTable
              columns={deliveryColumns}
              rows={deliveries}
              rowKey={(d) => d.event_id}
              emptyTitle="No delivery events"
              emptyDescription="Sends will appear here with channel, status, and TCPA context."
            />
          )}
        </Card>
      ) : (
        <Card title="Message templates" padded={false}>
          {!templatesAvailable ? (
            <div style={{ padding: 24 }}>
              <p style={styles.muted}>Templates are not available yet.</p>
            </div>
          ) : (
            <DataTable
              columns={templateColumns}
              rows={templates}
              rowKey={(t) => t.template_id}
              emptyTitle="No templates"
            />
          )}
        </Card>
      )}
    </>
  );
}

function Kpi({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div style={styles.kpiCard}>
      <div style={{ ...styles.kpiValue, color: accent ?? colors.textPrimary }}>{value}</div>
      <div style={styles.kpiLabel}>{label}</div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button type="button" onClick={onClick} style={{ ...styles.tabBtn, ...(active ? styles.tabBtnActive : null) }}>
      {children}
    </button>
  );
}

const styles: Record<string, CSSProperties> = {
  error: { color: colors.danger, fontSize: '0.9rem', margin: '0 0 12px' },
  muted: { color: colors.textSecondary, fontSize: '0.82rem' },
  mono: { fontFamily: 'monospace', fontSize: '0.82rem' },
  kpiStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 12,
    marginBottom: 24,
  },
  kpiCard: {
    background: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.md,
    padding: '14px 16px',
  },
  kpiValue: { fontSize: '1.6rem', fontWeight: 700, lineHeight: 1.1 },
  kpiLabel: { fontSize: '0.78rem', color: colors.textSecondary, marginTop: 4 },
  tabs: { display: 'flex', gap: 4 },
  tabBtn: {
    padding: '7px 14px',
    background: 'transparent',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.pill,
    fontSize: '0.85rem',
    fontWeight: 600,
    color: colors.textSecondary,
    cursor: 'pointer',
  },
  tabBtnActive: { background: colors.brandTint, color: colors.brandActive, borderColor: colors.brandTint },
};
